import type { AssociatedVehicleInside } from "../../../../../passapp-sdk/types/userTypes";
import type { GuardiaRow } from "../GuardiaTable";

/** Patente a mostrar en la fila. Sin `inside` cuando no hay dato de si la unidad sigue adentro. */
export interface PersonPlate {
  plate: string;
  inside?: boolean;
}

const upper = (plate?: string | null): string => String(plate ?? "").toUpperCase();

/**
 * Acoplados guardados en el evento. Los ingresos viejos guardaron sólo el id del vehículo, y de
 * esos no hay patente para mostrar. No traen estado: el evento es una foto del ingreso y no sabe
 * si la unidad ya salió.
 */
const trailerPlatesFromEvent = (row: GuardiaRow): string[] =>
  (row.event?.trailers ?? [])
    .map((trailer) => (typeof trailer === "string" ? undefined : trailer?.plate))
    .filter((plate): plate is string => Boolean(plate));

/**
 * Unidades con las que entró la persona, en el orden en que se muestran: primero la principal y
 * después las que entraron con ella.
 *
 * `associatedVehicles` es la fuente buena —la resuelve el backend contra el último evento de cada
 * vehículo, así que sabe cuáles siguen adentro— e incluye la unidad principal. Cuando no viene
 * (ingresos viejos, flujos que no son de transporte) se cae a las patentes del evento, sin estado.
 */
export const getPersonPlates = (row: GuardiaRow): PersonPlate[] => {
  const main = upper(row.patente);
  const asociadas: AssociatedVehicleInside[] = row.associatedVehicles ?? [];

  const plates: PersonPlate[] =
    asociadas.length > 0
      ? asociadas
          .filter((unit) => unit?.plate)
          // La principal encabeza la celda; el resto queda en el orden del backend (tractor y
          // después acoplados).
          .sort((a, b) => Number(upper(b.plate) === main) - Number(upper(a.plate) === main))
          .map((unit) => ({ plate: unit.plate, inside: unit.inside }))
      : trailerPlatesFromEvent(row).map((plate) => ({ plate }));

  // La principal puede no estar en la lista —el acompañante hereda el vehículo del chofer, y los
  // ingresos viejos no traen `associatedVehicles`—, así que se antepone si falta.
  if (row.patente && !plates.some((entry) => upper(entry.plate) === main)) {
    plates.unshift({ plate: row.patente });
  }

  const seen = new Set<string>();
  return plates.filter((entry) => {
    const key = upper(entry.plate);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};
